import { CheckCircle2, Circle, Loader2 } from "lucide-react";

/**
 * 5-stage customer-facing progress bar for the public quote page.
 * Sticky at the top, mobile-first. Tells the customer exactly where their
 * car is in the workshop's process without them having to ring up.
 *
 * Stages map to existing job timestamps:
 *   1. Checked in     → checked_in_at
 *   2. Inspected      → quote is built (status != "draft") OR video uploaded
 *   3. Awaiting you   → status == "sent" or "viewed" (no response yet)
 *   4. In progress    → customer has approved/partially-approved
 *   5. Ready          → completed_at set
 */
export default function CustomerProgressBar({ job }) {
  if (!job) return null;

  const checkedIn   = !!job.checked_in_at;
  const inspected   = checkedIn && (!!job.video_path || (job.items || []).length > 0 || ["sent", "viewed", "approved", "partial", "rejected", "needs_advice", "completed"].includes(job.status));
  const awaiting    = inspected && ["sent", "viewed", "needs_advice"].includes(job.status);
  const inProgress  = inspected && ["approved", "partial"].includes(job.status) && !job.completed_at;
  const ready       = !!job.completed_at;

  // The "current" stage is the latest one that's "in progress" right now.
  // We mark prior stages as done, the current as "active" (animated), the rest as future.
  let currentIdx = 0;
  if (ready) currentIdx = 4;
  else if (inProgress) currentIdx = 3;
  else if (awaiting) currentIdx = 2;
  else if (inspected) currentIdx = 1;
  else if (checkedIn) currentIdx = 0;

  const stages = [
    { id: "checked-in", label: "Checked in",          short: "Checked in",   done: checkedIn },
    { id: "inspected",  label: "Inspected by tech",   short: "Inspected",    done: inspected },
    { id: "awaiting",   label: "Awaiting your reply", short: "Your approval", done: ready || inProgress || !awaiting && inspected /* skipped past */ || awaiting },
    { id: "in-progress",label: "Work in progress",    short: "In progress",  done: ready || inProgress },
    { id: "ready",      label: "Ready for collection",short: "Ready",        done: ready },
  ];
  // Rewrite "awaiting" done logic — awaiting is "done" only once they've replied
  stages[2].done = ready || inProgress;

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-5 shadow-sm" data-testid="customer-progress-bar">
      {/* Mobile: stacked vertical-style chip row */}
      <div className="flex items-center justify-between gap-1.5">
        {stages.map((s, idx) => {
          const isActive = idx === currentIdx && !s.done;
          const isPast = s.done;
          const isFuture = !isPast && !isActive;
          return (
            <div key={s.id} className="flex items-center flex-1 min-w-0" data-testid={`progress-stage-${s.id}`} data-state={isPast ? "done" : isActive ? "active" : "pending"}>
              <div className="flex flex-col items-center text-center flex-shrink-0 min-w-0">
                <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full grid place-items-center transition-all ${
                  isPast    ? "bg-success text-success-foreground"
                  : isActive ? "bg-primary text-primary-foreground ring-4 ring-primary/20 animate-pulse"
                  :            "bg-muted text-muted-foreground"
                }`}>
                  {isPast ? <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" /> :
                   isActive ? <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" /> :
                   <Circle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                </div>
                <span className={`mt-1.5 text-[10px] sm:text-xs font-semibold leading-tight max-w-[5rem] sm:max-w-[8rem] ${
                  isFuture ? "text-muted-foreground" : "text-foreground"
                }`}>
                  <span className="sm:hidden">{s.short}</span>
                  <span className="hidden sm:inline">{s.label}</span>
                </span>
              </div>
              {idx < stages.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 sm:mx-2 mb-5 rounded-full transition-colors ${
                  stages[idx + 1].done || (idx + 1 === currentIdx && !stages[idx + 1].done && (isPast || isActive))
                    ? "bg-success"
                    : "bg-border"
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Status one-liner under the bar */}
      <div className="mt-3 text-center text-xs sm:text-sm text-muted-foreground" data-testid="progress-status-line">
        {ready          ? <><span className="font-bold text-success">Your car is ready</span> — please give us a ring or pop in to collect it.</>
        : inProgress    ? <><span className="font-bold text-primary">We're working on your car now</span> — we'll text you the moment it's ready.</>
        : awaiting      ? <><span className="font-bold text-primary">Your quote's ready</span> — please review the items below and tap Approve or Decline.</>
        : inspected     ? <>We've finished the inspection — your quote will be with you any moment.</>
        : checkedIn     ? <>Your car's safely with us. Our technician is starting the health-check now.</>
                        : <>Welcome — your car will be checked in shortly.</>}
      </div>
    </div>
  );
}
